import { addClaimToToken, auth, getCurrentUser } from '@/services/firebase/init'
import { reportError, setTelemetryUser } from '@/services/utils/telemetry'
import { defineStore } from 'pinia'
import { useMainStore } from './main'
import { isEmptyString } from '@/services/utils/string'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    isCheckingUserStatus: false,
    error: null,
    isTokenHasClaim: false,
    isHandlingCustomClaims: false,
  }),
  actions: {
    async isUserSignedIn() {
      this.isCheckingUserStatus = true
      try {
        let user = auth.currentUser
        if (user === null) {
          user = await getCurrentUser()
        }

        const mainStore = useMainStore()
        if (user && isEmptyString(mainStore.userEmail)) {
          setTelemetryUser({
            email: user.email,
            id: user.uid,
            name: user.displayName,
          })
          mainStore.setUser({
            email: user.email,
            name: user.displayName ?? user.email,
          })
        }
        return user !== null
      } catch (error) {
        console.error(error)
        reportError(error, {
          description: 'Error while checking user sign in status',
        })
        return false
      } finally {
        this.isCheckingUserStatus = false
      }
    },

    async checkTokenCustomClaims() {
      if (this.isTokenHasClaim) {
        return true
      }
      const user = auth.currentUser
      this.isHandlingCustomClaims = true
      const idTokenResult = await user.getIdTokenResult(true)
      if (idTokenResult?.claims?.app === 'RUBIX') {
        this.isTokenHasClaim = true
        return true
      }

      return false
    },

    async handleCustomClaims() {
      try {
        if (await this.checkTokenCustomClaims()) {
          this.isHandlingCustomClaims = false
          return
        }
      } catch (error) {
        reportError(error, {
          description: 'Error while checking user claims',
        })
        this.isHandlingCustomClaims = false
        throw error
      }

      try {
        await addClaimToToken()
      } catch (error) {
        reportError(error, {
          description: 'Error while adding custom claim to token',
        })
        this.isHandlingCustomClaims = false
        throw error
      }

      try {
        const user = auth.currentUser
        await user.getIdToken(true)
        this.isHandlingCustomClaims = false
      } catch (error) {
        reportError(error)
        throw error
      } finally {
        this.isHandlingCustomClaims = false
      }
    },
  },
})
